/**
 * The entry portal's backdrop, drawn on one 2D canvas: a deep horizon glow, a skyline far
 * away, a floor of light lines running to the horizon, a tilted ring of light turning slowly
 * above it and particles drifting up. It pauses when the tab or the portal is hidden and
 * draws a single still frame when the visitor prefers reduced motion.
 */
import { TOKENS } from '../ui/theme.ts';

interface Particle {
  x: number;
  y: number;
  r: number;
  speed: number;
  phase: number;
}

export function startPortalBackground(canvas: HTMLCanvasElement, isVisible: () => boolean): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  let w = 0;
  let h = 0;
  let dpr = 1;
  let skyline: HTMLCanvasElement | undefined;
  const particles: Particle[] = [];
  let seed = 7;
  const rand = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };

  const resize = (): void => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = canvas.clientWidth;
    h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    skyline = paintSkyline(w, h, dpr);
    particles.length = 0;
    const count = Math.round(Math.min(160, (w * h) / 9000));
    for (let i = 0; i < count; i++)
      particles.push({
        x: rand() * w,
        y: rand() * h,
        r: 0.6 + rand() * 1.6,
        speed: 4 + rand() * 14,
        phase: rand() * Math.PI * 2,
      });
  };

  const draw = (t: number): void => {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const horizon = h * 0.64;
    const cx = w / 2;
    // Sky: charcoal above, a blue-cyan glow toward the horizon.
    const sky = ctx.createLinearGradient(0, 0, 0, horizon);
    sky.addColorStop(0, TOKENS.void);
    sky.addColorStop(0.7, '#0a1320');
    sky.addColorStop(1, '#0f2335');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, horizon);
    const glow = ctx.createRadialGradient(cx, horizon, 0, cx, horizon, Math.max(w, h) * 0.6);
    glow.addColorStop(0, 'rgba(63,214,255,0.22)');
    glow.addColorStop(0.35, 'rgba(61,123,255,0.08)');
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);
    if (skyline) ctx.drawImage(skyline, 0, 0, w, h);
    // Floor below the horizon.
    const floor = ctx.createLinearGradient(0, horizon, 0, h);
    floor.addColorStop(0, '#0b1724');
    floor.addColorStop(1, TOKENS.void);
    ctx.fillStyle = floor;
    ctx.fillRect(0, horizon, w, h - horizon);
    // Floor lines: rays to the vanishing point and depth lines running toward the viewer.
    ctx.lineWidth = 1;
    for (let i = -14; i <= 14; i++) {
      const x = cx + i * (w / 9);
      const a = 0.16 * (1 - Math.abs(i) / 15);
      ctx.strokeStyle = `rgba(63,214,255,${a.toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(cx, horizon);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    const travel = (t * 0.00006) % 1;
    for (let k = 0; k < 9; k++) {
      const p = (k / 9 + travel) % 1;
      const y = horizon + (h - horizon) * p * p;
      ctx.strokeStyle = `rgba(155,234,255,${(0.04 + p * 0.14).toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    // Horizon line.
    const hl = ctx.createLinearGradient(0, 0, w, 0);
    hl.addColorStop(0, 'rgba(63,214,255,0)');
    hl.addColorStop(0.5, 'rgba(155,234,255,0.7)');
    hl.addColorStop(1, 'rgba(63,214,255,0)');
    ctx.fillStyle = hl;
    ctx.fillRect(0, horizon - 0.5, w, 1);
    // The ring of light, tilted and turning, with its reflection under the horizon.
    const rx = Math.min(w * 0.42, h * 0.62);
    const ry = rx * 0.26;
    const cy = horizon - ry * 1.9;
    drawRing(ctx, cx, cy, rx, ry, t * 0.00012, 1);
    drawRing(ctx, cx, cy, rx * 0.72, ry * 0.72, -t * 0.00018, 0.6);
    drawRing(ctx, cx, horizon + ry * 1.2, rx, ry * 0.55, t * 0.00012, 0.12);
    // Particles drifting up, twinkling.
    for (const p of particles) {
      const y = (((p.y - t * 0.001 * p.speed) % h) + h) % h;
      const a = 0.25 + 0.35 * Math.sin(t * 0.0015 + p.phase);
      ctx.fillStyle = `rgba(155,234,255,${Math.max(0, a).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(p.x, y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Vignette.
    const v = ctx.createRadialGradient(
      cx,
      h * 0.5,
      Math.min(w, h) * 0.3,
      cx,
      h * 0.5,
      Math.max(w, h) * 0.75,
    );
    v.addColorStop(0, 'rgba(0,0,0,0)');
    v.addColorStop(1, 'rgba(0,0,0,0.6)');
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, w, h);
  };

  resize();
  window.addEventListener('resize', () => {
    resize();
    if (reduced) draw(0);
  });
  if (reduced) {
    draw(0);
    return;
  }
  const loop = (t: number): void => {
    if (isVisible() && !document.hidden) draw(t);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

/** A ring of light as arcs of varying brightness, plus a few bright nodes riding it. */
function drawRing(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  angle: number,
  strength: number,
): void {
  const segments = 72;
  ctx.lineCap = 'round';
  for (let i = 0; i < segments; i++) {
    const a0 = angle + (i / segments) * Math.PI * 2;
    const a1 = a0 + (Math.PI * 2) / segments + 0.002;
    // Brighter at the front (bottom of the ellipse), with a travelling band.
    const front = 0.5 + 0.5 * Math.sin(a0);
    const band = 0.5 + 0.5 * Math.cos(i * 0.35);
    const a = strength * (0.15 + 0.55 * front) * (0.55 + 0.45 * band);
    ctx.strokeStyle = `rgba(63,214,255,${a.toFixed(3)})`;
    ctx.lineWidth = 1.2 + front * 1.6;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, a0, a1);
    ctx.stroke();
  }
  // Soft halo.
  ctx.strokeStyle = `rgba(63,214,255,${(0.06 * strength).toFixed(3)})`;
  ctx.lineWidth = 14;
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  for (let k = 0; k < 4; k++) {
    const a = angle * 2.2 + (k * Math.PI) / 2;
    const x = cx + Math.cos(a) * rx;
    const y = cy + Math.sin(a) * ry;
    const s = 0.5 + 0.5 * Math.sin(a);
    ctx.fillStyle = `rgba(242,246,251,${(strength * (0.3 + 0.7 * s)).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, 1.5 + s * 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** A far skyline along the horizon: slim towers with a few lit windows, painted once. */
function paintSkyline(w: number, h: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.round(w * dpr);
  c.height = Math.round(h * dpr);
  const g = c.getContext('2d')!;
  g.scale(dpr, dpr);
  const horizon = h * 0.64;
  let s = 41;
  const r = (): number => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  let x = -20;
  while (x < w + 20) {
    const bw = 10 + r() * 26;
    const centre = 1 - Math.abs(x - w / 2) / (w / 2);
    const bh = (14 + r() * 60 + centre * 70) * (h / 900);
    const shade = 14 + Math.floor(r() * 10);
    g.fillStyle = `rgb(${shade},${shade + 8},${shade + 18})`;
    g.fillRect(x, horizon - bh, bw, bh);
    // A thin crown light on some towers.
    if (r() < 0.25) {
      g.fillStyle = 'rgba(63,214,255,0.65)';
      g.fillRect(x + bw * 0.2, horizon - bh - 1, bw * 0.6, 1.2);
    }
    g.fillStyle = 'rgba(155,234,255,0.22)';
    for (let wy = horizon - bh + 4; wy < horizon - 3; wy += 5)
      for (let wx = x + 2; wx < x + bw - 2; wx += 4) if (r() < 0.12) g.fillRect(wx, wy, 1.5, 1.5);
    x += bw + r() * 6;
  }
  return c;
}
